<template>
    <div class="fulfillment-batch-content">
        <section class="vertical-align">
            <label class="batch-title">{{ $t('funnel.edit.fulfillment.batch.title') }}</label>
            <p>{{ $t('funnel.edit.fulfillment.batch.summary') }}</p>
        </section>
        <section>
            <div class="vertical-align">
                <DsCheckbox
                    :value="fulfillmentObject.batch"
                    data-qa="fulfillment-batch"
                    :label="$t('funnel.edit.fulfillment.batch.job')"
                    @input="updateBatchCheckbox"
                />
                <label class="batch-subtitle" :class="{ 'disabled': batchDisabled }">
                    {{ $t('funnel.edit.fulfillment.batch.subtitle') }}
                </label>
                <div
                    data-qa="batch-options"
                    :class="{ 'disabled': batchDisabled }"
                >
                    <div class="horizontal-align radio-group-spacing">
                        <DsRadio
                            v-model="batchType"
                            data-qa="fulfillment-batch-groups"
                            :disabled="batchDisabled"
                            class="batch-type-item"
                            :val="$options.BATCH_TYPE.ONLY_GROUPS"
                            :label="$t('funnel.edit.fulfillment.batch.groups')"
                        />
                        <DsInputField
                            v-if="!onlyGroupsDisabled"
                            :value="fulfillmentObject.minItems"
                            data-qa="fulfillment-batch-groups-items"
                            :readonly="batchDisabled"
                            class="batch-type-input"
                            type="number"
                            name="number"
                            :step="1"
                            :min="1"
                            :max="9999"
                            @input="minItemsGroupsInput"
                        />
                        <DsInputField
                            v-else
                            v-model="minItemsGroups"
                            :readonly="true"
                            class="batch-type-input"
                            type="number"
                            name="number"
                            :step="1"
                            :min="1"
                            :max="9999"
                        />
                    </div>
                    <div class="horizontal-align radio-group-spacing">
                        <DsRadio
                            v-model="batchType"
                            data-qa="fulfillment-batch-days"
                            :disabled="batchDisabled"
                            class="batch-type-item"
                            :val="$options.BATCH_TYPE.ONLY_DAYS"
                            :label="$t('funnel.edit.fulfillment.batch.every')"
                        />
                        <DsInputField
                            v-if="!onlyDaysDisabled"
                            :value="fulfillmentObject.maxWaitDays"
                            :readonly="batchDisabled"
                            class="batch-type-input"
                            type="number"
                            name="number"
                            :step="1"
                            :min="1"
                            :max="9999"
                            @input="maxWaitDaysInput"
                        />
                        <DsInputField
                            v-else
                            v-model="maxWaitDays"
                            :readonly="true"
                            class="batch-type-input"
                            type="number"
                            name="number"
                            :step="1"
                            :min="1"
                            :max="9999"
                        />
                        <span class="detail-info">
                            {{ $t('funnel.edit.fulfillment.batch.days') }}
                        </span>
                    </div>
                    <div class="horizontal-align radio-group-spacing">
                        <DsRadio
                            v-model="batchType"
                            data-qa="fulfillment-batch-groups-days"
                            :disabled="batchDisabled"
                            class="batch-type-item"
                            :val="$options.BATCH_TYPE.GROUPS_DAYS"
                            :label="$t('funnel.edit.fulfillment.batch.groups')"
                        />
                        <DsInputField
                            v-if="!groupsAndDaysDisabled"
                            :value="fulfillmentObject.minItems"
                            data-qa="fulfillment-batch-items-input"
                            :readonly="batchDisabled"
                            class="batch-type-input"
                            type="number"
                            name="number"
                            :step="1"
                            :min="1"
                            :max="9999"
                            @input="groupsAndDaysItemsInput"
                        />
                        <DsInputField
                            v-else
                            v-model="minItemsDaysAndGroups"
                            :readonly="true"
                            class="batch-type-input"
                            type="number"
                            name="number"
                            :step="1"
                            :min="1"
                            :max="9999"
                        />
                        <span class="detail-info">
                            {{ $t('funnel.edit.fulfillment.batch.wait') }}
                        </span>
                        <DsInputField
                            v-if="!groupsAndDaysDisabled"
                            :value="fulfillmentObject.maxWaitDays"
                            data-qa="fulfillment-batch-days-input"
                            :readonly="batchDisabled"
                            class="batch-type-input"
                            type="number"
                            name="number"
                            :step="1"
                            :min="1"
                            :max="9999"
                            @input="groupsAndDaysWaitInput"
                        />
                        <DsInputField
                            v-else
                            v-model="maxWaitDaysAndGroups"
                            :readonly="true"
                            class="batch-type-input"
                            type="number"
                            name="number"
                            :step="1"
                            :min="1"
                            :max="9999"
                        />
                        <span class="detail-info">
                            {{ $t('funnel.edit.fulfillment.batch.days') }}
                        </span>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
export const BATCH_TYPE = {
    ONLY_GROUPS: '0',
    ONLY_DAYS: '1',
    GROUPS_DAYS: '2',
};

export const BATCH_VALUES = {
    WAITING_DAYS_DEFAULT: 7,
    WAITING_DAYS_THRESHOLD: 7300,
    CONTACT_GROUPS_DEFAULT: 100,
    CONTACT_GROUPS_THRESHOLD: 100000,
};

export default {
    BATCH_TYPE,
    BATCH_VALUES,

    props: {
        cell: {
            type: Object,
            required: true,
        },
        fulfillmentObject: {
            type: Object,
            required: true,
        },
    },

    emits: ['update-fulfillment-object'],

    data() {
        return {
            batchType: BATCH_TYPE.GROUPS_DAYS,
            maxWaitDays: BATCH_VALUES.WAITING_DAYS_DEFAULT,
            minItemsGroups: BATCH_VALUES.CONTACT_GROUPS_DEFAULT,
            maxWaitDaysAndGroups: BATCH_VALUES.WAITING_DAYS_DEFAULT,
            minItemsDaysAndGroups: BATCH_VALUES.CONTACT_GROUPS_DEFAULT,
        };
    },

    computed: {
        batchDisabled() {
            return !this.fulfillmentObject.batch;
        },
        onlyGroupsDisabled() {
            return this.batchType !== BATCH_TYPE.ONLY_GROUPS;
        },
        onlyDaysDisabled() {
            return this.batchType !== BATCH_TYPE.ONLY_DAYS;
        },
        groupsAndDaysDisabled() {
            return this.batchType !== BATCH_TYPE.GROUPS_DAYS;
        },
    },

    watch: {
        batchType(newValue, oldValue) {
            if (newValue === oldValue || !oldValue) {
                return;
            }

            switch (newValue) {
            case BATCH_TYPE.ONLY_GROUPS:
                this.setGroupsInitialBatchValues();
                break;
            case BATCH_TYPE.ONLY_DAYS:
                this.setDaysInitialBatchValues();
                break;
            case BATCH_TYPE.GROUPS_DAYS:
                this.setGroupAndDaysInitialBatchValues();
                break;
            default:
                break;
            }
        },
    },

    mounted() {
        this.batchType = this.getBatchType();
        this.setBatchOptions();
    },

    methods: {
        updateBatchCheckbox(value) {
            this.$emit('update-fulfillment-object', {
                ...this.fulfillmentObject,
                batch: value,
            });
        },

        getBatchType() {
            let type = BATCH_TYPE.GROUPS_DAYS;

            if (this.fulfillmentObject.maxWaitDays >= BATCH_VALUES.WAITING_DAYS_THRESHOLD) {
                type = BATCH_TYPE.ONLY_GROUPS;
            }

            if (this.fulfillmentObject.minItems >= BATCH_VALUES.CONTACT_GROUPS_THRESHOLD) {
                type = BATCH_TYPE.ONLY_DAYS;
            }

            return type;
        },

        setBatchOptions() {
            if (!this.groupsAndDaysDisabled) {
                this.maxWaitDaysAndGroups = this.fulfillmentObject.maxWaitDays;
                this.minItemsDaysAndGroups = this.fulfillmentObject.minItems;
            }

            if (!this.onlyDaysDisabled) {
                this.maxWaitDays = this.fulfillmentObject.maxWaitDays;
            }

            if (!this.onlyGroupsDisabled) {
                this.minItemsGroups = this.fulfillmentObject.minItems;
            }
        },

        setGroupAndDaysInitialBatchValues() {
            this.$emit('update-fulfillment-object', {
                ...this.fulfillmentObject,
                minItems: this.minItemsDaysAndGroups,
                maxWaitDays: this.maxWaitDaysAndGroups,
            });
        },

        setGroupsInitialBatchValues() {
            this.$emit('update-fulfillment-object', {
                ...this.fulfillmentObject,
                maxWaitDays: BATCH_VALUES.WAITING_DAYS_THRESHOLD,
                minItems: this.minItemsGroups,
            });
        },

        setDaysInitialBatchValues() {
            this.$emit('update-fulfillment-object', {
                ...this.fulfillmentObject,
                minItems: BATCH_VALUES.CONTACT_GROUPS_THRESHOLD,
                maxWaitDays: this.maxWaitDays,
            });
        },

        groupsAndDaysWaitInput(value) {
            this.maxWaitDaysAndGroups = value;
            this.$emit('update-fulfillment-object', {
                ...this.fulfillmentObject,
                maxWaitDays: value,
            });
        },

        groupsAndDaysItemsInput(value) {
            this.minItemsDaysAndGroups = value;
            this.$emit('update-fulfillment-object', {
                ...this.fulfillmentObject,
                minItems: value,
            });
        },

        maxWaitDaysInput(value) {
            this.maxWaitDays = value;
            this.$emit('update-fulfillment-object', {
                ...this.fulfillmentObject,
                maxWaitDays: value,
            });
        },

        minItemsGroupsInput(value) {
            this.minItemsGroups = value;
            this.$emit('update-fulfillment-object', {
                ...this.fulfillmentObject,
                minItems: value,
            });
        },
    },
};
</script>
<style lang="scss" scoped>
.fulfillment-batch-content {
    max-width: px-to-rem(592);

    section {
        padding-bottom: $spacing-300;
    }

    .horizontal-align {
        display: flex;
    }

    .radio-group-spacing {
        padding-bottom: $spacing-100;
    }

    .vertical-align {
        label.batch-subtitle {
            font-size: $font-size-lg;
            font-weight: 400;
            line-height: $line-height-lg;
            padding-bottom: $spacing-200;
        }
    }

    label.batch-title {
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 0 auto;
        font-size: $font-size-jumbo;
        font-weight: 400;
        line-height: $line-height-xxl;
        padding-bottom: $spacing-100;
    }

    p {
        display: flex;
        justify-content: center;
        align-items: center;
        text-align: center;
        margin: 0 auto;
        font-size: $font-size-md;
        font-weight: 400;
        line-height: $line-height-lg;
    }

    .batch-type-input {
        width: px-to-rem(75);
        margin: 0;
        padding: px-to-rem(4) 0;
    }

    .detail-info {
        padding: $spacing-200 $spacing-050 0 $spacing-050;
    }

    .disabled {
        color: $color-gray-700;
    }
}
</style>
